<template>
  <v-dialog v-model="dialogVisible" max-width="750" persistent>
    <v-card>
      <v-card-title>{{ isEdit ? 'Editeaza Regula Alerta Trafic' : 'Regula Noua Alerta Trafic' }}</v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12">
            <v-text-field v-model="form.name" label="Nume Regula *" density="compact" :rules="[v => !!v || 'Obligatoriu']" />
          </v-col>
          <v-col cols="12">
            <v-textarea v-model="form.description" label="Descriere" density="compact" rows="2" auto-grow />
          </v-col>
        </v-row>

        <!-- Threshold settings -->
        <v-row dense>
          <v-col cols="12" md="6">
            <v-select v-model="form.metric_type" label="Metrica *" :items="metricTypes" item-title="title" item-value="value" density="compact" :rules="[v => !!v || 'Obligatoriu']" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model.number="form.threshold_value" label="Prag *" type="number" density="compact" :suffix="metricUnit" :rules="[v => v > 0 || 'Trebuie > 0']" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="form.network_type" label="Tip Retea" :items="networkTypes" item-title="title" item-value="value" density="compact" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="form.period_type" label="Perioada" :items="periodTypes" item-title="title" item-value="value" density="compact" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="form.action_type" label="Actiune *" :items="actionTypes" item-title="title" item-value="value" density="compact" :rules="[v => !!v || 'Obligatoriu']" />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model.number="form.cooldown_hours" label="Cooldown (ore)" type="number" density="compact" hint="Nu re-alerteaza in X ore" />
          </v-col>
        </v-row>

        <!-- Target: client + scope + optional group/sim -->
        <v-card variant="outlined" class="mt-3 pa-3">
          <div class="text-subtitle-2 mb-2">Tinta alertei</div>
          <v-row dense>
            <v-col cols="12">
              <ClientSearchFilter
                v-model="form.managed_by_client_id"
                :show-include-subclients="false"
                client-select-label="Client *"
                @hierarchy-loaded="onHierarchyLoaded"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="form.scope_type" label="Aplicare" :items="scopeTypes" item-title="title" item-value="value" density="compact" />
            </v-col>
            <v-col cols="12" md="6">
              <v-select v-model="targetType" label="Filtreaza pe" :items="targetOptions" item-title="title" item-value="value" density="compact" />
            </v-col>
            <v-col cols="12" md="6" v-if="targetType === 'imsi_group'">
              <v-autocomplete
                v-model="form.imsi_group_id"
                label="Grup IMSI"
                :items="clientImsiGroups"
                item-title="name"
                item-value="id"
                density="compact"
                :loading="loadingGroups"
                clearable
                no-data-text="Niciun grup gasit pentru acest client"
              />
            </v-col>
            <v-col cols="12" md="6" v-if="targetType === 'sim'">
              <v-autocomplete
                v-model="form.sim_id"
                label="SIM"
                :items="clientSimsList"
                item-title="label"
                item-value="id"
                density="compact"
                :loading="loadingSims"
                clearable
                no-data-text="Niciun SIM gasit pentru acest client"
              />
            </v-col>
          </v-row>
        </v-card>

        <v-row dense class="mt-2">
          <v-col cols="12" md="6">
            <v-text-field v-model.number="form.priority" label="Prioritate" type="number" density="compact" hint="0 = cea mai mare" />
          </v-col>
          <v-col cols="12" md="6">
            <v-switch v-model="form.enabled" label="Activa" color="primary" density="compact" hide-details />
          </v-col>
          <v-col cols="12" v-if="showProfileSelect">
            <v-select v-model="form.limitation_profile_id" label="Profil Limitari (optional)" :items="profileOptions" item-title="name" item-value="id" density="compact" clearable />
          </v-col>
        </v-row>

        <v-expansion-panels class="mt-2" variant="accordion">
          <v-expansion-panel title="Notificare Personalizata (optional)">
            <v-expansion-panel-text>
              <v-text-field v-model="form.notification_subject" label="Subiect notificare" density="compact" hint="Variabile: {client_name}, {sim_msisdn}, {metric_label}, {current_value}, {threshold_value}, {unit}" persistent-hint class="mb-2" />
              <v-textarea v-model="form.notification_body" label="Corp notificare" density="compact" rows="4" auto-grow hint="Variabile: {client_name}, {sim_msisdn}, {sim_id}, {metric_label}, {current_value}, {threshold_value}, {unit}, {network_type}, {period_type}" persistent-hint />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="close">Anuleaza</v-btn>
        <v-btn color="primary" variant="flat" @click="save" :loading="saving" :disabled="!isValid">{{ isEdit ? 'Salveaza' : 'Creeaza' }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {createTrafficAlertRule, updateTrafficAlertRule, getClientImsiGroups, getClientSims} from '@/composables/useTrafficAlertsAPI'
import ClientSearchFilter from '@/components/ClientSearchFilter.vue'

export default {
  name: 'TrafficAlertRuleDialog',
  components: { ClientSearchFilter },
  props: {
    modelValue: Boolean,
    rule: { type: Object, default: null },
    metricTypes: { type: Array, default: () => [] },
    networkTypes: { type: Array, default: () => [] },
    periodTypes: { type: Array, default: () => [] },
    actionTypes: { type: Array, default: () => [] },
    scopeTypes: { type: Array, default: () => [] },
    profileOptions: { type: Array, default: () => [] },
    showProfileSelect: { type: Boolean, default: true },
    defaultProfileId: { type: Number, default: null }
  },
  emits: ['update:modelValue', 'saved'],
  data() {
    return {
      saving: false,
      targetType: 'none',
      clientImsiGroups: [],
      clientSims: [],
      loadingGroups: false,
      loadingSims: false,
      form: this.getEmptyForm()
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.modelValue },
      set(v) { this.$emit('update:modelValue', v) }
    },
    isEdit() { return !!this.rule?.id },
    isValid() {
      return this.form.name && this.form.metric_type && this.form.threshold_value > 0 && this.form.action_type && this.form.managed_by_client_id
    },
    metricUnit() {
      const units = { data_mb: 'MB', sms_count: 'msg', voice_minutes: 'min', total_cost: 'EUR' }
      return units[this.form.metric_type] || ''
    },
    targetOptions() {
      return [
        { title: 'Toate SIM-urile (din scope)', value: 'none' },
        { title: 'Grup IMSI specific', value: 'imsi_group' },
        { title: 'SIM specific', value: 'sim' }
      ]
    },
    clientSimsList() {
      return this.clientSims.map(s => ({
        id: s.id,
        label: s.msisdn ? `#${s.id} (${s.msisdn})` : `SIM #${s.id}`
      }))
    }
  },
  watch: {
    modelValue(v) {
      if (v) {
        if (this.rule?.id) {
          this.form = { ...this.rule }
          if (this.rule.sim_id) this.targetType = 'sim'
          else if (this.rule.imsi_group_id) this.targetType = 'imsi_group'
          else this.targetType = 'none'
        } else {
          this.form = this.getEmptyForm()
          this.targetType = 'none'
        }
        // Load groups/sims if client already set
        if (this.form.managed_by_client_id) {
          this.loadClientData(this.form.managed_by_client_id)
        }
      }
    },
    'form.managed_by_client_id'(val, oldVal) {
      if (val && val !== oldVal) {
        // Client changed - reload groups and sims, reset selections
        this.form.sim_id = null
        this.form.imsi_group_id = null
        this.clientImsiGroups = []
        this.clientSims = []
        this.loadClientData(val)
      }
    },
    targetType(val) {
      if (val === 'none') {
        this.form.sim_id = null
        this.form.imsi_group_id = null
      } else if (val === 'sim') {
        this.form.imsi_group_id = null
      } else if (val === 'imsi_group') {
        this.form.sim_id = null
      }
    }
  },
  methods: {
    getEmptyForm() {
      return {
        name: '', description: '', metric_type: 'data_mb', network_type: 'all',
        period_type: 'monthly', threshold_value: 0, action_type: 'notify_email',
        notification_subject: '', notification_body: '', enabled: true,
        priority: 0, cooldown_hours: 24, scope_type: 'client_only',
        sim_id: null, imsi_group_id: null, managed_by_client_id: null,
        limitation_profile_id: this.defaultProfileId
      }
    },
    onHierarchyLoaded({ currentClientId }) {
      // Auto-select current client if form has no client set
      if (!this.form.managed_by_client_id && currentClientId) {
        this.form.managed_by_client_id = currentClientId
      }
    },
    async loadClientData(clientId) {
      if (!clientId) return
      const token = this.$store.state.token
      this.loadingGroups = true
      this.loadingSims = true
      const [groupsResult, simsResult] = await Promise.all([
        getClientImsiGroups(token, clientId),
        getClientSims(token, clientId)
      ])
      if (!groupsResult.error) this.clientImsiGroups = groupsResult.imsi_groups || []
      if (!simsResult.error) this.clientSims = simsResult.sims || []
      this.loadingGroups = false
      this.loadingSims = false
    },
    close() {
      this.dialogVisible = false
    },
    async save() {
      this.saving = true
      const token = this.$store.state.token
      const data = { ...this.form }
      if (this.targetType === 'none') {
        data.sim_id = null
        data.imsi_group_id = null
      }
      let result
      if (this.isEdit) {
        result = await updateTrafficAlertRule(token, data)
      } else {
        result = await createTrafficAlertRule(token, data)
      }
      this.saving = false
      if (!result.error) {
        this.$emit('saved', result.rule)
        this.close()
      } else {
        alert(result.message)
      }
    }
  }
}
</script>
